import { A } from '@adornis/base/env-info.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { context } from '@adornis/baseql/server/context.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { filter, firstValueFrom, map } from 'rxjs';
import { SaraaUser, type UserWithMemberships } from './db/SaraaUser.js';

/**
 * The shape every check works against. Structural rather than the entity class,
 * because checks run over both hydrated entities on the client and raw Mongo
 * documents on the server.
 */
export type PermissionUser = UserWithMemberships;

/**
 * Sentinel passed as `data` when asking "may this user reach this page at all",
 * where there is no concrete entity to check against — ERM's `ViewRoute` and its
 * CRUD gates both evaluate without entity data.
 *
 * Checks that accept it must still verify *something* about the user (see
 * `isOrgAdminPageCheck`), otherwise a page check degenerates into "any user".
 */
export const PAGE_CHECK = { _meta: { pageCheck: true } } as const;

export interface PageCheckable {
  _meta?: { pageCheck?: boolean };
}

export const isPageCheckPayload = (data: Maybe<PageCheckable>) => data?._meta?.pageCheck === true;

/**
 * Isomorphic user lookup used by every scope policy.
 *
 * `defineScopePolicy`'s default is `CurrentUserInfo.getMyself()({})` — a GraphQL
 * round-trip per evaluation *with an empty selection set*, so it would not even
 * carry `memberships`. Overriding it is a correctness requirement, not just an
 * optimisation.
 *
 * On the client this reads the already-live `currentUser()` subscription, which
 * selects `SaraaUser.allFields` and therefore includes both `memberships` and
 * `isPlatformAdmin`. On the server it is a single indexed lookup by
 * `context.userID`.
 *
 * Because grants are now derived from these two fields alone, this is the whole
 * of permission resolution on the client — there is no second round trip to cache,
 * and a change to either field propagates through the live subscription
 * immediately rather than waiting out a session cache.
 */
export const fetchUser = async (): Promise<Maybe<PermissionUser>> => {
  if (A.isClient) {
    return firstValueFrom(
      currentUser<SaraaUser>().dataFeed.pipe(
        filter(feed => feed.fullyLoaded),
        map(feed => feed.data),
      ),
    );
  }

  // ServerContextPolicy has already granted anything running without a user, so
  // reaching here in a server context means a policy was evaluated outside a
  // request and the caller needs to know.
  if (context.serverContext) throw new Error('[saraa/permissions] fetchUser called in a server context');
  if (!context.userID) return null;

  const collection = await getRawCollection<EntityData<SaraaUser>>(SaraaUser._collectionName);
  return collection.findOne({ _id: context.userID }, { projection: { _id: 1, memberships: 1, isPlatformAdmin: 1 } });
};

/**
 * There is no anonymous access in saraa — the whole app sits behind
 * `authenticationGuard` — so an unauthenticated evaluation is always a denial.
 * (The reference implementation models anonymous access as a `noUser` role; we
 * have no use for one.)
 */
export const noUserHandler = async () => ({ allowed: false, reason: 'not authenticated' });
